import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert
} from '@mui/material';
import { expenseService, customerService } from '../../services/financeService';
import { buildExpenseRows } from '../../utils/exportUtils';
import CsvActions from '../common/CsvActions';
import { checkAdminStatus } from '../../utils/authUtils';
import AttachmentPanel from './AttachmentPanel';
import './Financial.css';

/**
 * Expense Management Component
 * Full CRUD for expenses with approval workflow
 */
const ExpenseManagement = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  const userData = localStorage.getItem('userData');
  const { isAdmin } = checkAdminStatus(userData);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [expensesRes, categoriesRes] = await Promise.all([
        isAdmin ? expenseService.getAll() : expenseService.getMy(),
        expenseService.getCategories()
      ]);

      if (expensesRes.success) setExpenses(expensesRes.data || []);
      if (categoriesRes.success) setCategories(categoriesRes.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'PKR 0';
    const n = Number(amount);
    const abs = Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 2 });
    return n < 0 ? `-PKR ${abs}` : `PKR ${abs}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB');
  };

  const getStatusBadge = (status) => {
    const badges = {
      PENDING: 'badge-pending',
      APPROVED: 'badge-approved',
      REJECTED: 'badge-overdue',
      PAID: 'badge-paid',
      CANCELLED: 'badge-draft'
    };
    return badges[status] || 'badge-draft';
  };

  const filteredExpenses = expenses.filter(exp => {
    if (activeTab === 'all') return true;
    if (activeTab === 'pending') return exp.status === 'PENDING';
    if (activeTab === 'approved') return exp.status === 'APPROVED';
    if (activeTab === 'rejected') return exp.status === 'REJECTED';
    return true;
  });

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null, danger: false });
  // Reject dialog state
  const [rejectDialog, setRejectDialog] = useState({ open: false, expenseId: null, reason: '' });

  const showConfirm = (title, message, onConfirm, danger = false) => {
    setConfirmDialog({ open: true, title, message, onConfirm, danger });
  };
  const closeConfirm = () => setConfirmDialog(prev => ({ ...prev, open: false }));

  const handleApprove = (id) => {
    showConfirm('Approve Expense', 'Are you sure you want to approve this expense?', async () => {
      closeConfirm();
      try { await expenseService.approve(id); showToast('Expense approved'); fetchData(); }
      catch (err) { setError(err.message); }
    });
  };

  const handleReject = (id) => {
    setRejectDialog({ open: true, expenseId: id, reason: '' });
  };

  const submitReject = async () => {
    if (!rejectDialog.reason.trim()) return;
    try {
      await expenseService.reject(rejectDialog.expenseId, rejectDialog.reason);
      setRejectDialog({ open: false, expenseId: null, reason: '' });
      showToast('Expense rejected');
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = (id) => {
    showConfirm('Delete Expense', 'Are you sure you want to delete this expense? This cannot be undone.', async () => {
      closeConfirm();
      try { await expenseService.delete(id); showToast('Expense deleted'); fetchData(); }
      catch (err) { setError(err.message); }
    }, true);
  };

  if (loading) {
    return (
      <div className="expense-management">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span>Loading expenses...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="expense-management">
      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, background: toast.type === 'error' ? 'var(--color-error-light)' : 'var(--color-success-light)', color: toast.type === 'error' ? 'var(--color-error)' : 'var(--color-success)', padding: '12px 20px', borderRadius: 8, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          {toast.msg}
        </div>
      )}
      <div className="page-header">
        <h2>Expense Management</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {(() => { const { headers, matrix } = buildExpenseRows(filteredExpenses); return (
            <CsvActions filename="expenses" headers={headers} matrix={matrix} />
          ); })()}
          <button onClick={() => { setEditingExpense(null); setShowForm(true); }} className="btn-primary">
            + New Expense
          </button>
        </div>
      </div>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Tabs */}
      <div className="tab-navigation">
        <button className={`tab-button ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>
          All ({expenses.length})
        </button>
        <button className={`tab-button ${activeTab === 'pending' ? 'active' : ''}`} onClick={() => setActiveTab('pending')}>
          Pending ({expenses.filter(e => e.status === 'PENDING').length})
        </button>
        <button className={`tab-button ${activeTab === 'approved' ? 'active' : ''}`} onClick={() => setActiveTab('approved')}>
          Approved ({expenses.filter(e => e.status === 'APPROVED').length})
        </button>
        <button className={`tab-button ${activeTab === 'rejected' ? 'active' : ''}`} onClick={() => setActiveTab('rejected')}>
          Rejected ({expenses.filter(e => e.status === 'REJECTED').length})
        </button>
      </div>

      {/* Expense Table */}
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Expense #</th>
              <th>Title</th>
              <th>Category</th>
              <th>Date</th>
              <th>Amount</th>
              <th>Submitted By</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredExpenses.length === 0 ? (
              <tr>
                <td colSpan="8">
                  <div className="empty-state">
                    <div className="empty-state-icon">—</div>
                    <h3>No expenses found</h3>
                    <p>Submit your first expense to get started</p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredExpenses.map(expense => (
                <tr key={expense.id}>
                  <td><strong>{expense.expenseNumber}</strong></td>
                  <td>{expense.title}</td>
                  <td>{expense.category}</td>
                  <td>{formatDate(expense.expenseDate)}</td>
                  <td>{formatCurrency(expense.totalAmount)}</td>
                  <td>{expense.userName}</td>
                  <td>
                    <span className={getStatusBadge(expense.status)}>
                      {expense.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {expense.status === 'PENDING' && (
                        <>
                          <button onClick={() => { setEditingExpense(expense); setShowForm(true); }} className="action-btn btn-secondary">
                            Edit
                          </button>
                          {isAdmin && (
                            <>
                              <button onClick={() => handleApprove(expense.id)} className="action-btn btn-success">
                                Approve
                              </button>
                              <button onClick={() => handleReject(expense.id)} className="action-btn btn-danger">
                                Reject
                              </button>
                            </>
                          )}
                          <button onClick={() => handleDelete(expense.id)} className="action-btn btn-danger">
                            Delete
                          </button>
                        </>
                      )}
                      {expense.status === 'REJECTED' && expense.rejectionReason && (
                        <span title={expense.rejectionReason} className="action-btn btn-secondary" style={{ cursor: 'help', fontSize: '0.75rem' }}>View Reason</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Expense Form Modal */}
      {showForm && (
        <ExpenseForm
          expense={editingExpense}
          categories={categories}
          onClose={() => { setShowForm(false); setEditingExpense(null); }}
          onSave={() => { setShowForm(false); setEditingExpense(null); showToast(editingExpense ? 'Expense updated' : 'Expense created'); fetchData(); }}
        />
      )}

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={closeConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent><p style={{ margin: 0, color: 'var(--color-neutral-700)' }}>{confirmDialog.message}</p></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <button onClick={closeConfirm} className="btn-secondary">Cancel</button>
          <button onClick={confirmDialog.onConfirm} className={confirmDialog.danger ? 'btn-danger' : 'btn-primary'}>Confirm</button>
        </DialogActions>
      </Dialog>

      {/* Reject Reason Dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog(r => ({ ...r, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Expense</DialogTitle>
        <DialogContent>
          <p style={{ margin: '0 0 12px', color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>Please provide a reason for rejection:</p>
          <TextField
            label="Rejection Reason"
            value={rejectDialog.reason}
            onChange={e => setRejectDialog(r => ({ ...r, reason: e.target.value }))}
            fullWidth size="small" multiline rows={2} autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <button onClick={() => setRejectDialog(r => ({ ...r, open: false }))} className="btn-secondary">Cancel</button>
          <button onClick={submitReject} className="btn-danger" disabled={!rejectDialog.reason.trim()}>Reject</button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

/**
 * Expense Form Modal
 */
const ExpenseForm = ({ expense, categories, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    category: expense?.category || '',
    title: expense?.title || '',
    description: expense?.description || '',
    amount: expense?.amount || '',
    taxAmount: expense?.taxAmount || 0,
    expenseDate: expense?.expenseDate || new Date().toISOString().split('T')[0],
    paymentMethod: expense?.paymentMethod || 'CASH',
    referenceNumber: expense?.referenceNumber || '',
    vendorName: expense?.vendorName || '',
    notes: expense?.notes || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [customVendor, setCustomVendor] = useState('');

  useEffect(() => {
    customerService.getAll().then(res => {
      const list = res?.data || res || [];
      const all = Array.isArray(list) ? list : [];
      setVendors(all.filter(c => c.customerType === 'VENDOR' || c.customerType === 'BOTH'));
    }).catch(() => {});
  }, []);

  const paymentMethods = ['CASH', 'BANK_TRANSFER', 'CREDIT_CARD', 'DEBIT_CARD', 'CHECK', 'ONLINE', 'PETTY_CASH', 'OTHER'];

  // Cost of Goods Sold: direct trade costs, tied to a specific batch/shipment.
  // Operating Expenses: general overhead, not tied to any one shipment.
  // These match com.chatbot.enums.ExpenseCategory on the backend one-for-one, so every
  // category here posts to its own Chart of Accounts leaf instead of one generic bucket.
  const cogsCategories = ['LABOR', 'PACKAGING', 'FREIGHT', 'SORTING_RECOVERY'];
  const operatingCategories = ['TRAVEL', 'SUPPLIES', 'UTILITIES', 'MEALS', 'TRANSPORTATION', 'OFFICE', 'EQUIPMENT', 'OTHER'];
  const defaultCategories = [...cogsCategories, ...operatingCategories];
  // Any legacy/free-text category already used historically still shows up (grouped separately below).
  const extraCategories = categories.filter(c => !defaultCategories.includes(c)).sort();

  const categoryLabels = {
    LABOR: 'Labor (Loading/Sorting)',
    PACKAGING: 'Packaging (Bardana)',
    FREIGHT: 'Freight & Container',
    SORTING_RECOVERY: 'Sorting/Grading Recovery',
    TRAVEL: 'Travel', SUPPLIES: 'Supplies', UTILITIES: 'Utilities', MEALS: 'Meals',
    TRANSPORTATION: 'Transportation', OFFICE: 'Office', EQUIPMENT: 'Equipment', OTHER: 'Other'
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      const resolvedVendor = formData.vendorName === '__other__' ? customVendor.trim() : formData.vendorName;
      const data = {
        ...formData,
        vendorName: resolvedVendor || null,
        amount: parseFloat(formData.amount),
        taxAmount: parseFloat(formData.taxAmount) || 0
      };

      if (expense) {
        await expenseService.update(expense.id, data);
      } else {
        await expenseService.create(data);
      }
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{expense ? 'Edit Expense' : 'Submit New Expense'}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="error-message" style={{ marginBottom: '16px' }}><p>{error}</p></div>}

          <div className="form-group">
            <label>Vendor / Payee
              <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)', fontWeight: 400, marginLeft: 6 }}>who received this payment</span>
            </label>
            {vendors.length > 0 ? (
              <select name="vendorName" value={formData.vendorName} onChange={handleChange} className="form-select">
                <option value="">— Select Vendor / Payee (optional) —</option>
                {vendors.map(v => <option key={v.id} value={v.name}>{v.name}{v.customerCode ? ` [${v.customerCode}]` : ''}</option>)}
                <option value="__other__">Other (type below)</option>
              </select>
            ) : (
              <input type="text" name="vendorName" value={formData.vendorName} onChange={handleChange} className="form-input" placeholder="Vendor or payee name" />
            )}
            {formData.vendorName === '__other__' && (
              <input type="text" className="form-input" style={{ marginTop: 6 }} placeholder="Enter vendor/payee name..." value={customVendor} onChange={e => setCustomVendor(e.target.value)} autoFocus />
            )}
          </div>

          <div className="form-group">
            <label>Title *</label>
            <input type="text" name="title" value={formData.title} onChange={handleChange} className="form-input" required placeholder="Brief expense title" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>Category *</label>
              <select name="category" value={formData.category} onChange={handleChange} className="form-select" required>
                <option value="">Select Category</option>
                <optgroup label="Cost of Goods Sold">
                  {cogsCategories.map(c => (
                    <option key={c} value={c}>{categoryLabels[c] || c}</option>
                  ))}
                </optgroup>
                <optgroup label="Operating Expenses">
                  {operatingCategories.map(c => (
                    <option key={c} value={c}>{categoryLabels[c] || c}</option>
                  ))}
                </optgroup>
                {extraCategories.length > 0 && (
                  <optgroup label="Other (legacy)">
                    {extraCategories.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>

            <div className="form-group">
              <label>Date *</label>
              <input type="date" name="expenseDate" value={formData.expenseDate} onChange={handleChange} className="form-input" required />
            </div>

            <div className="form-group">
              <label>Amount *</label>
              <input type="number" name="amount" value={formData.amount} onChange={handleChange} className="form-input" min="0.01" step="0.01" required placeholder="0.00" />
            </div>

            <div className="form-group">
              <label>Tax Amount</label>
              <input type="number" name="taxAmount" value={formData.taxAmount} onChange={handleChange} className="form-input" min="0" step="0.01" placeholder="0.00" />
            </div>

            <div className="form-group">
              <label>Payment Method</label>
              <select name="paymentMethod" value={formData.paymentMethod} onChange={handleChange} className="form-select">
                {paymentMethods.map(m => (
                  <option key={m} value={m}>{m.replace('_', ' ')}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Reference Number</label>
              <input type="text" name="referenceNumber" value={formData.referenceNumber} onChange={handleChange} className="form-input" placeholder="Receipt/Invoice number" />
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea name="description" value={formData.description} onChange={handleChange} className="form-textarea" rows="3" placeholder="Detailed description of the expense" />
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea name="notes" value={formData.notes} onChange={handleChange} className="form-textarea" rows="2" placeholder="Additional notes" />
          </div>

          <div style={{ background: 'var(--color-neutral-50)', padding: '16px', borderRadius: '8px', marginTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.1rem', fontWeight: 'bold' }}>
              <span>Total Amount:</span>
              <span>PKR {((parseFloat(formData.amount) || 0) + (parseFloat(formData.taxAmount) || 0)).toLocaleString()}</span>
            </div>
          </div>

          {/* Attachments (receipts, invoices, etc.) - only show when editing */}
          {expense && expense.id && (
            <div style={{ marginTop: '20px', borderTop: '1px solid var(--color-border-medium)', paddingTop: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px', color: 'var(--color-neutral-700)' }}>
                Attachments <span style={{ fontWeight: 400, fontSize: '12px', color: 'var(--color-text-secondary)' }}>(receipts, invoices, etc.)</span>
              </h4>
              <AttachmentPanel entityType="EXPENSE" entityId={expense.id} />
            </div>
          )}

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : (expense ? 'Update Expense' : 'Submit Expense')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpenseManagement;

